import {
  ControlledMenu,
  SubMenu,
  MenuDivider,
} from '@szhsin/react-menu'
import type { MenuInstance } from '@szhsin/react-menu'
import { useDisclosure, Tooltip, Text } from '@chakra-ui/react'
import type { ReactElement } from 'react'
import { useContext, useRef, } from 'react'
import type {
  MediaLibraryModalsControllers,
} from '../../../interfaces'
import { MLContext, UserContext } from '../../../providers'
import type { MediaLibraryActionType } from '../../../providers/MLProvider'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import AutoTagsModal from '../Details/AutoTagsModal'
import RemoveBgModal from '../Details/RemoveBgModal'
import {
  CopyMoveDrawer,
  CustomFoucsAreaModal,
  DeleteMediaModal,
  DownloadModal,
  RenameFileModal,
  RenameFolderModal,
  RenameMediaCollectionModal,
  TagsModal,
  ShareAssetModal,
  ShareMediaCollectionModal,
  ApplySavedExtensionModal,
} from '../MediaOperations'
import DeleteFileVersionModal from '../MediaOperations/DeleteFileVersionModal'
import RestoreFileVersionModal from '../MediaOperations/RestoreFileVersionModal'
import PublishFilesModal from '../MediaOperations/PublishFilesModal'
import PhotoEditor from '../PhotoEditor'
import {
  AddToMediaCollection,
  RemoveAssetFromMultipleMediaCollections,
  RemoveMultipleAssetsFromMediaCollection,
} from '../../MediaCollections'
import PublicLinksModal from '../MediaOperations/PublicLinksModal'
import CopyEmbedURLModal from '../CopyEmbedURLModal'
import ApplyPathPolicyModal from '../../PathPolicy/ApplyPathPolicyModal'
import {useContextMenuItems} from '../hooks/useContextMenuItems'
import MenuItemWithTooltip from './MenuItem'
import ImageEditor from '../ImageEditor'
import { colors } from '../../../../ui-kit'
import BulkEditCustomMetadataModal from '../MediaOperations/BulkEditCustomMetadataModal'

const ContextMenu = ({controllers}:{  controllers: MediaLibraryModalsControllers}): ReactElement => {
  const [
    {
      contextMenu,
      selectedFiles,
      selectedFolders,
      selectedMediaCollections,
      refetchFiles,
    },
    dispatch,
  ] = useContext(MLContext)
  
  const [openEmbedModal, handleEmbedModal] =controllers.embedModal

  const [tagOperation, setTagOperation] = controllers.tagState
  const [copyMoveOperation, setCopyMoveOperation] = controllers.copyMoveState


  const {
    isOpen: isOpenAddToMediaCollection,
    onOpen: onOpenAddToMediaCollection,
    onClose: onCloseAddToMediaCollection,
  } = controllers.addToMediaCollection

  const {
    isOpen: isOpenRenameFileModal,
    onOpen: onOpenRenameFileModal,
    onClose: onCloseRenameFileModal,
  } = controllers.renameFile

  const {
    isOpen: isOpenRenameFolderModal,
    onOpen: onOpenRenameFolderModal,
    onClose: onCloseRenameFolderModal,
  } = controllers.renameFolder

  const {
    isOpen: isOpenPublishFilesModal,
    onOpen: onOpenPublishFilesModal,
    onClose: onClosePublishFilesModal,
  } =controllers.publishFiles

  const {
    isOpen: isOpenTagsModal,
    onOpen: onOpenTagsModal,
    onClose: onCloseTagsModal,
  } = controllers.tags

  const {
    isOpen: isOpenAutoTagsModal,
    onOpen: onOpenAutoTagsModal,
    onClose: onCloseAutoTagsModal,
  } = controllers.autoTags

  const {
    isOpen: isOpenRemoveAssetFromMultipleMediaCollections,
    onOpen: onOpenRemoveAssetFromMultipleMediaCollections,
    onClose: onCloseRemoveAssetFromMultipleMediaCollections,
  } = controllers.removeAssetFromMultipleMediaCollections

  const {
    isOpen: isOpenRemoveMultipleAssetsFromMediaCollection,
    onOpen: onOpenRemoveMultipleAssetsFromMediaCollection,
    onClose: onCloseRemoveMultipleAssetsFromMediaCollection,
  } = controllers.removeMultipleAssetsFromMediaCollection

  const {
    isOpen: isOpenDeleteMediaModal,
    onOpen: onOpenDeleteMediaModal,
    onClose: onCloseDeleteMediaModal,
  } = controllers.deleteMedia

  const {
    isOpen: isOpenDeleteFileVersionModal,
    onOpen: onOpenDeleteFileVersionModal,
    onClose: onCloseDeleteFileVersionModal,
  } = controllers.deleteFileVersion

  const {
    isOpen: isOpenRestoreFileVersionModal,
    onOpen: onOpenRestoreFileVersionModal,
    onClose: onCloseRestoreFileVersionModal,
  } = controllers.restoreFileVersion

  const {
    isOpen: isOpenCopyMoveDrawer,
    onOpen: onOpenCopyMoveDrawer,
    onClose: onCloseCopyMoveDrawer,
  } = controllers.copyMoveDrawer

  const {
    isOpen: isOpenPhotoEditor,
    onOpen: onOpenPhotoEditor,
    onClose: onClosePhotoEditor,
  } = controllers.photoEditor

  const {
    isOpen: isOpenImageEditor,
    onOpen: onOpenImageEditor,
    onClose: onCloseImageEditor,
  } = controllers.imageEditor

  const {
    isOpen: isOpenBulkEditCustomMetadataModal,
    onOpen: onOpenBulkEditCustomMetadataModal,
    onClose: onCloseBulkEditCustomMetadataModal,
  } = controllers.bulkEditCustomMetadata

  const {
    isOpen: isOpenRemoveBgModal,
    onOpen: onOpenRemoveBgModal,
    onClose: onCloseRemoveBgModal,
  } = controllers.removeBg

  const {
    isOpen: isOpenCustomFocusAreaModal,
    onOpen: onOpenCustomFocusAreaModal,
    onClose: onCloseCustomFocusAreaModal,
  } = controllers.customFocusArea

  const {
    isOpen: isOpenShareAssetModal,
    onOpen: onOpenShareAssetModal,
    onClose: onCloseShareAssetModal,
  } = controllers.shareAsset

  const {
    isOpen: isOpenShareMediaCollectionModal,
    onOpen: onOpenShareMediaCollectionModal,
    onClose: onCloseShareMediaCollectionModal,
  } = controllers.shareMediaCollection

  const {
    isOpen: isOpenPublicLinksModal,
    onOpen: onOpenPublicLinksModal,
    onClose: onClosePublicLinkModal,
  } = controllers.publicLinks

  const {
    isOpen: isOpenRenameMediaCollectionModal,
    onOpen: onOpenRenameMediaCollectionModal,
    onClose: onCloseRenameMediaCollectionModal,
  } = controllers.renameMediaCollection
  const {
    isOpen: isOpenApplyPathPolicyModal,
    onOpen: onOpenApplyPathPolicyModal,
    onClose: onCloseApplyPathPolicyModal,
  } = controllers.applyPathPolicy

  const {
    isOpen: isOpenApplySavedExtensionModal,
    onOpen: onOpenApplySavedExtensionModal,
    onClose: onCloseApplySavedExtensionModal,
  } = controllers.applySavedExtension

    const {
    isOpen: isOpenDownloadModal,
    onOpen: onOpenDownloadModal,
    onClose: onCloseDownloadModal,
  } = controllers.download

  const refMap = {
    organize: useRef<MenuInstance>(null),
    edit: useRef<MenuInstance>(null),
    tags: useRef<MenuInstance>(null),
    download: useRef<MenuInstance>(null),
    share: useRef<MenuInstance>(null),
  }

      const isFileVersionSelected =
      selectedFiles.filter(item => item.type === 'file-version').length > 0


  const contextMenuItemList = useContextMenuItems({
    setCopyMoveOperation,
    setTagOperation,
    onOpenCopyMoveDrawer,
    onOpenRenameFileModal,
    onOpenRenameFolderModal,
    onOpenRenameMediaCollectionModal,
    onOpenPublishFilesModal,
    onOpenTagsModal,
    onOpenAutoTagsModal,
    onOpenRemoveAssetFromMultipleMediaCollections,
    onOpenRemoveMultipleAssetsFromMediaCollection,
    onOpenDeleteMediaModal,
    onOpenDeleteFileVersionModal,
    onOpenRestoreFileVersionModal,
    onOpenRemoveBgModal,
    onOpenCustomFocusAreaModal,
    onOpenShareAssetModal,
    onOpenShareMediaCollectionModal,
    onOpenPublicLinksModal,
    onOpenAddToMediaCollection,
    onOpenDownloadModal,
    handleEmbedModal,
    onOpenApplyPathPolicyModal,
    onOpenApplySavedExtensionModal,
    onOpenImageEditor,
    onOpenPhotoEditor,
    onOpenBulkEditCustomMetadataModal,
  refMap})

   const filteredItems = contextMenuItemList.filter(x => x.viewConditions);

  return (
    <>
      {contextMenu.visible &&
        (filteredItems.length > 0 ||
          (isFileVersionSelected && filteredItems.length === 0)) && (
          <>
            <ControlledMenu
              submenuCloseDelay={0}
              submenuOpenDelay={0}
              menuStyle={{
                padding: '4px 0px',
                borderRadius: 8,
                boxShadow: 'none',
                border: `1px solid ${colors.appLightGreyTwo}`,
                minWidth: 200,
              }}
              onMouseLeave={() => {
                Object.values(refMap).forEach(ref => ref?.current?.closeMenu())
              }}
              viewScroll="close"
              anchorPoint={{
                x: contextMenu.position.x,
                y: contextMenu.position.y,
              }}
              overflow="auto"
              position="auto"
              state={contextMenu.visible ? 'open' : 'closed'}
              onClose={() => {
                dispatch({ type: MediaLibraryActions.CLOSE_CONTEXT_MENU })
              }}
            >
              {(() => {
                if (filteredItems.length === 0) {
                  return (
                    <Text px={3} py={2} color="gray.500" fontSize="sm">
                      {isFileVersionSelected
                        ? 'Unselect the file version to see options.'
                        : 'No options available'}
                    </Text>
                  )
                }

                return filteredItems.map((x, ind) => {
                  if (x.subMenu) {
                    return (
                      <SubMenu
                        position="anchor"
                        menuStyle={{
                          padding: 4,
                          borderRadius: 8,
                          boxShadow: 'none',
                          border: `1px solid ${colors.appLightGreyTwo}`,
                          minWidth: x.subMenu.minWidth || undefined,
                        }}
                        instanceRef={x.reference}
                        label={x.element?.({})}
                        gap={0}
                        key={`submenu-${ind}`}
                      >
                        {x?.subMenu?.items
                          ?.filter(item => item.viewConditions)
                          .map((item, itemInd) => {
                            if (item.name === 'divider') {
                              return (
                                <MenuDivider
                                  key={`submenu-divider-${ind}-${itemInd}`}
                                />
                              )
                            }
                            return (
                              <MenuItemWithTooltip
                                key={`submenu-item-${ind}-${itemInd}`}
                                item={item}
                                ind={ind}
                                itemInd={itemInd}
                              />
                            )
                          })}
                      </SubMenu>
                    )
                  } else if (x.name === 'divider') {
                    return <MenuDivider key={`divider-${ind}`} />
                  } else {
                    return <MenuItemWithTooltip key={`menu-item-${ind}`} item={x} ind={ind} />
                  }
                })
              })()}
            </ControlledMenu>
          </>
        )}

      {/* Modals wrapped with isOpen condition */}

      <AddToMediaCollection
        isOpen={isOpenAddToMediaCollection}
        onClose={onCloseAddToMediaCollection}
        onOpen={onOpenAddToMediaCollection}
      />

      {isOpenRemoveAssetFromMultipleMediaCollections && (
        <RemoveAssetFromMultipleMediaCollections
          isOpen={isOpenRemoveAssetFromMultipleMediaCollections}
          onClose={onCloseRemoveAssetFromMultipleMediaCollections}
        />
      )}

      {isOpenRemoveMultipleAssetsFromMediaCollection && (
        <RemoveMultipleAssetsFromMediaCollection
          isOpen={isOpenRemoveMultipleAssetsFromMediaCollection}
          onClose={onCloseRemoveMultipleAssetsFromMediaCollection}
        />
      )}

      {isOpenRenameFileModal && (
        <RenameFileModal
          isOpen={isOpenRenameFileModal}
          onClose={onCloseRenameFileModal}
        />
      )}
      {isOpenRenameFolderModal && (
        <RenameFolderModal
          isOpen={isOpenRenameFolderModal}
          onClose={onCloseRenameFolderModal}
        />
      )}

      {isOpenRenameMediaCollectionModal && (
        <RenameMediaCollectionModal
          isOpen={isOpenRenameMediaCollectionModal}
          onClose={onCloseRenameMediaCollectionModal}
        />
      )}

      {isOpenDeleteMediaModal && (
        <DeleteMediaModal
          isOpen={isOpenDeleteMediaModal}
          onClose={onCloseDeleteMediaModal}
        />
      )}

      {isOpenTagsModal && (
        <TagsModal
          operation={tagOperation}
          isOpen={isOpenTagsModal}
          onClose={onCloseTagsModal}
        />
      )}

      {isOpenAutoTagsModal && (
        <AutoTagsModal
          fileId={selectedFiles[0]?.fileId}
          isOpen={isOpenAutoTagsModal}
          onClose={onCloseAutoTagsModal}
        />
      )}

      {isOpenCopyMoveDrawer && (
        <CopyMoveDrawer
          operation={copyMoveOperation}
          isOpen={isOpenCopyMoveDrawer}
          onClose={onCloseCopyMoveDrawer}
        />
      )}

      <CustomFoucsAreaModal
        isOpen={isOpenCustomFocusAreaModal}
        onClose={onCloseCustomFocusAreaModal}
        fileDetails={selectedFiles[0]}
      />

      {isOpenDeleteFileVersionModal && (
        <DeleteFileVersionModal
          isOpen={isOpenDeleteFileVersionModal}
          onClose={onCloseDeleteFileVersionModal}
        />
      )}

      {isOpenRestoreFileVersionModal && (
        <RestoreFileVersionModal
          isOpen={isOpenRestoreFileVersionModal}
          onClose={onCloseRestoreFileVersionModal}
        />
      )}

      {isOpenRemoveBgModal && (
        <RemoveBgModal
          isOpen={isOpenRemoveBgModal}
          onClose={onCloseRemoveBgModal}
          fileId={selectedFiles[0]?.fileId}
        />
      )}

      {isOpenShareAssetModal && (
        <ShareAssetModal
          isOpen={isOpenShareAssetModal}
          onClose={onCloseShareAssetModal}
          assetId={selectedFiles[0]?.fileId ?? selectedFolders[0]?.folderId}
          assetName={selectedFiles[0]?.name ?? selectedFolders[0]?.name}
          assetType={selectedFiles[0]?.type ?? selectedFolders[0]?.type}
        />
      )}

      {isOpenShareMediaCollectionModal && (
        <ShareMediaCollectionModal
          isOpen={isOpenShareMediaCollectionModal}
          onClose={onCloseShareMediaCollectionModal}
          assetId={selectedMediaCollections[0]?.mediaCollectionId}
          assetName={selectedMediaCollections[0]?.name}
        />
      )}

      {isOpenPublicLinksModal && (
        <PublicLinksModal
          assetId={
            selectedFiles[0]?.fileId ??
            selectedFolders[0]?.folderId ??
            selectedMediaCollections[0]?.mediaCollectionId
          }
          assetType={
            selectedFiles[0]?.type ??
            selectedFolders[0]?.type ??
            'media-collection'
          }
          assetName={
            selectedFiles[0]?.name ??
            selectedFolders[0]?.name ??
            selectedMediaCollections[0]?.name
          }
          isOpen={isOpenPublicLinksModal}
          onClose={onClosePublicLinkModal}
        />
      )}

      {isOpenPublishFilesModal && (
        <PublishFilesModal
          isOpen={isOpenPublishFilesModal}
          onClose={onClosePublishFilesModal}
        />
      )}

      {isOpenApplyPathPolicyModal && (
        <ApplyPathPolicyModal
          isOpen={isOpenApplyPathPolicyModal}
          onClose={onCloseApplyPathPolicyModal}
        />
      )}

      {isOpenApplySavedExtensionModal && (
        <ApplySavedExtensionModal
          isOpen={isOpenApplySavedExtensionModal}
          onClose={onCloseApplySavedExtensionModal}
        />
      )}

      {selectedFiles && selectedFiles.length && openEmbedModal ? (
        <CopyEmbedURLModal
          file={selectedFiles[0]}
          openEmbedModal={openEmbedModal}
          handleEmbedModal={handleEmbedModal}
        />
      ) : null}

      {isOpenPhotoEditor && (
        <PhotoEditor onClose={onClosePhotoEditor} dispatch={dispatch} />
      )}

      {isOpenImageEditor && <ImageEditor onClose={onCloseImageEditor} />}

      {isOpenDownloadModal && (
        <DownloadModal
          isOpen={isOpenDownloadModal}
          onClose={onCloseDownloadModal}
        />
      )}

      {isOpenBulkEditCustomMetadataModal && (
        <BulkEditCustomMetadataModal
          isOpen={isOpenBulkEditCustomMetadataModal}
          onClose={onCloseBulkEditCustomMetadataModal}
          selectedFiles={selectedFiles}
        />
      )}
    </>
  )
}

export default ContextMenu
